import React from 'react'
import { Helmet } from 'react-helmet'
import { Box, List, ListItem, Typography } from '@mui/material'

import { ContentTestimonial, ClientProps } from './ContentTestimonial'
import { DynamicInputForm, FormProps } from './DynamicInputForm'
import { PortableText } from './PortableText'

import Image from '@components/Image'

export type LeftRightContentWithFormLogosProps = {
  data: {
    title: string
    title_category: string
    description: object
    customer_logos: {
      _key: string; asset: {
        url: string
        altText: string
      }
    }[]
    input_form: FormProps['formProps']
    testimonial: ClientProps
  }
  location: { href: string; pathname: string }
}

export const LeftRightContentWithFormLogos = ({
  data,
  location,
}: LeftRightContentWithFormLogosProps) => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Us | Contact Rudderstack Team',
    url: 'https://www.rudderstack.com/contact/',
  }

  return (
    <Box sx={{ overflowX: 'hidden', pt: { xs: 8.5, laptop: 13 } }}>
      <Helmet htmlAttributes={{ lang: 'en' }}>
        {location?.href && location?.pathname === '/contact/' && (
          <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
        )}
      </Helmet>

      <Box
        sx={{
          display: { mobile: 'flex' },
          maxWidth: 1152,
          mx: 'auto',
          position: 'relative',
        }}>
        <Box
          sx={{
            width: { mobile: '40%' },
            pb: { xs: 5, mobile: 25 },
            pl: { xs: 3, mobile: 5, desktop: 0 },
            pr: { xs: 3, mobile: 5, laptop: 9 },
            pt: { xs: 5, laptop: 9 },
          }}>
          {data.title_category && (
            <Typography
              variant='subtitle2'
              sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              {data.title_category.toUpperCase()}
            </Typography>
          )}

          <Typography
            variant='h1'
            sx={{
              fontSize: { xs: 24, laptop: 54 },
              mt: { xs: 1, laptop: 1.5 },
            }}>
            {data.title}
          </Typography>

          <PortableText
            blocks={data.description}
            trackSectionHeader={data.title}
            sx={{ mt: { xs: 2, laptop: 3 }, opacity: 0.8 }}
          />

          <Box sx={{ display: { mobile: 'flex' } }}>
            <List
              disablePadding
              sx={{
                display: 'flex',
                flexDirection: { mobile: 'column' },
                gap: { xs: 4, mobile: 1.5, laptop: 3 },
                justifyContent: 'space-between',
                mr: { xs: 4, mobile: 0 },
                mt: { xs: 3, mobile: 5, laptop: 8 },
              }}>
              {data.customer_logos
                .filter((_, index) => index % 2 === 0)
                .map(item => (
                  <ListItem
                    key={item._key}
                    disablePadding
                    sx={{
                      width: 'fit-content',
                      height: { mobile: 40 },
                      maxWidth: 180
                    }}
                  >
                    <Box component={Image} alt={item?.asset?.altText} url={item?.asset?.url} />
                  </ListItem>
                ))}
            </List>

            <List
              disablePadding
              sx={{
                alignItems: { mobile: 'flex-end' },
                display: 'flex',
                flexDirection: { mobile: 'column' },
                gap: { xs: 4, mobile: 1.5, laptop: 3 },
                justifyContent: 'space-between',
                mt: { xs: 3, mobile: 5, laptop: 8 },
                ml: { xs: 4, mobile: 2 },
              }}>
              {data.customer_logos
                .filter((_, index) => index % 2 > 0)
                .map(item => (
                  <ListItem
                    key={item._key}
                    disablePadding
                    sx={{
                      width: 'fit-content',
                      height: { mobile: 40 },
                      maxWidth: 180
                    }}
                  >
                    <Box component={Image} url={item.asset?.url} alt={item.asset?.altText} />
                  </ListItem>
                ))}
            </List>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            bgcolor: 'text.primary',
            position: { mobile: 'absolute' },
            left: { mobile: '40%' },
          }}>
          <DynamicInputForm
            formProps={data.input_form}
            sx={{
              bgcolor: 'transparent',
              border: 0,
              maxWidth: { mobile: '60%' },
              pr: { desktop: 0 },
              px: { xs: 5, mobile: 5.5, laptop: 12 },
              py: { xs: 5.5, mobile: 4.5, laptop: 9 },
              '.MuiTypography-body1': { color: 'background.default' },
            }}
          />
        </Box>
      </Box>

      <ContentTestimonial
        isContactTestimonial
        {...data.testimonial}
        sx={{ px: { xs: 4, mobile: 6, laptop: 10 } }}
        sxContainer={{ px: { xs: 3, mobile: 5, desktop: 0 } }}
      />
    </Box>
  )
}
